import * as React from "react";
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HelpTooltip } from "../help/HelpTooltip";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldValues, FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  name: TName;
  label: React.ReactNode;
  options: SelectOption[];
  required?: boolean;
  tooltip?: string;
  placeholder?: string;
};

export function SelectInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  label,
  options,
  required,
  tooltip,
  placeholder,
}: SelectInputProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const { t } = useTranslation();

  const translateError = (m?: string) => (m ? t(m, { defaultValue: m }) : "");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;

        return (
          <Box>
            <Box
              sx={{
                mb: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: 14, fontWeight: 600, color: "#343A40" }}
                >
                  {label}
                  {required && (
                    <Box component="span" sx={{ color: "#F05252", ml: 0.25 }}>
                      *
                    </Box>
                  )}
                </Typography>

                {tooltip && (
                  <HelpTooltip
                    title={tooltip}
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={2000}
                  >
                    <IconButton
                      size="small"
                      sx={{ p: 0.25, ml: 0.25, color: "#6A7A89" }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </HelpTooltip>
                )}
              </Box>
            </Box>

            <FormControl fullWidth error={!!fieldState.error}>
              <Select
                {...field}
                displayEmpty
                value={field.value ?? ""}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  height: 46,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E4EDF5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#B3C6DA",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#3975D0",
                    boxShadow: "0 0 0 1px rgba(57, 117, 208, 0.15)",
                  },
                  "& .MuiSelect-select": {
                    color: field.value ? "#1A1A1A" : "#A0AEC0",
                    fontSize: { xs: 16, md: 14 },
                  },
                }}
              >
                {placeholder && (
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#A0AEC0", fontSize: 14 }}>
                      {placeholder}
                    </Typography>
                  </MenuItem>
                )}
                {options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errorMsg && (
                <FormHelperText>{translateError(errorMsg)}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );
      }}
    />
  );
}
